<template>
    <div
        ref="showMoreContentElement"
        :class="['show-more', {'show-more--collapsed': isShowMoreActivated && collapsed}]"
        :data-testid="`${props.dataTestid}-content`"
    >
        <slot class="show-more__content" />
        <div v-if="isShowMoreActivated" class="show-more__link">
            <ShowMoreLocked
                v-if="showMoreLock"
                :label="locked.label"
                :test-id="dataTestid"
                :href="lockedHref"
                :size="size"
                @click="handleClickedOnLocked"
            />
            <ShowMore v-else :collapsed="collapsed" :label="label" :test-id="dataTestid" :size="size" @click="handleClick" />
        </div>
    </div>
</template>
<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useRoute} from '#imports';
    import {type TJoyButtonSizes} from '@maltjoy/core-vue';
    import ShowMore from '~/components/_common/show-more/ShowMore.vue';
    import ShowMoreLocked from '~/components/_common/show-more/ShowMoreLocked.vue';
    import {useProfilePageViewMode} from '~/composables/useProfilePageViewMode';
    import {useSectionsVisibilityStore} from '~/store/sectionsVisibility';
    import {SectionsVisibilityType} from '~/types/domain/SectionsVisibility';

    export type ShowMoreByMaxHeightProps = {
        maxHeight?: number;
        dataTestid?: string;
        label?: string;
        size?: TJoyButtonSizes;
        displayAllInEditionMode?: boolean;
        locked:
            | {allowHref: false; label: string} // No href, event sent
            | {allowHref: true; label: string; componentName: string; targetId: string}; // default href
    };

    const props = withDefaults(defineProps<ShowMoreByMaxHeightProps>(), {
        maxHeight: 180,
        displayAllInEditionMode: true,
        dataTestid: 'show-more-component',
    });

    const emits = defineEmits<{
        (e: 'showMoreClickedWhenLocked'): void;
    }>();

    // Visibility
    const {isEditionEnabled} = useProfilePageViewMode();
    const isHigherThanDefinedMaxHeight = computed(() => showMoreContentHeight.value > props.maxHeight);
    const isShowMoreActivated = computed(() => (!props.displayAllInEditionMode || !isEditionEnabled.value) && isHigherThanDefinedMaxHeight.value);

    // Locked
    const {sectionVisibility} = storeToRefs(useSectionsVisibilityStore());
    const showMoreLock = computed(() => sectionVisibility.value[SectionsVisibilityType.VIEW_MORE_LOCK]);
    const route = useRoute();
    const lockedHref = computed(() => {
        if (!props.locked.allowHref) {
            return undefined;
        }
        const redirectParam = encodeURIComponent(`/profile${route.path}#${props.locked.targetId}`);
        return `/who-are-you?redirect=${redirectParam}&pageSource=profile_page&componentSource=${props.locked.componentName}`;
    });
    function handleClickedOnLocked() {
        emits('showMoreClickedWhenLocked');
    }

    // Height
    const showMoreContentElement = ref<HTMLElement>();
    const showMoreContentHeight = computed(() => showMoreContentElement.value?.scrollHeight || 0);
    const maxHeightCssVariable = `${props.maxHeight}px`;

    // Collapsed
    const collapsed = ref<boolean>(true);
    function handleClick() {
        collapsed.value = !collapsed.value;
    }
</script>
<style scoped lang="scss">
    .show-more {
        position: relative;
        overflow: hidden;

        &__link {
            width: 100%;
            padding: var(--joy-core-spacing-4) 0 0 0;

            &-button {
                flex-wrap: wrap;
                white-space: pre-wrap;
            }
        }

        &--collapsed {
            max-height: v-bind(maxHeightCssVariable);
            .show-more__link {
                position: absolute;
                bottom: 0;
                left: 0;
                padding: var(--joy-core-spacing-8) 0 0 0;

                background: linear-gradient(0deg, rgba(255, 255, 255, 1) 75%, rgba(255, 255, 255, 0) 100%);
                z-index: calc(var(--header-z-index) - 2);
            }
        }
    }
</style>
